import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";

/**
 * Unit of measure for freight quantities
 */
export type UnitOfMeasure = "tons" | "lbs" | "each";

/**
 * Status of a freight matching group
 */
export type MatchingGroupStatus = "pending" | "partial" | "matched" | "discrepancy";

/**
 * Status of an individual freight invoice line
 */
export type FreightLineStatus = "pending" | "matched" | "discrepancy";

/**
 * Type of discrepancy found during matching
 */
export type DiscrepancyType = "quantity_mismatch" | "amount_mismatch" | "missing_freight" | "missing_vendor_line";

/**
 * Vendor Invoice Line - a line from the material supplier's invoice
 * e.g., Chelan Sand & Gravel billing for materials delivered
 */
export interface VendorInvoiceLine {
  id: string;
  matchingGroupId: string;
  lineNumber: number;
  loadDate: string; // Date the material was delivered
  materialCode: string; // e.g., "P-Gravel", "3/4 Rock"
  materialDescription: string;
  quantity: number;
  unitOfMeasure: UnitOfMeasure;
  unitPrice: number;
  amount: number;
  matchedFreightLineId?: string; // Link to matched freight invoice line
  status: FreightLineStatus;
}

/**
 * Freight Invoice Line - a line from the trucking company's invoice
 * e.g., Walton Trucking, Pitton Trucking billing for hauling
 */
export interface FreightInvoiceLine {
  id: string;
  matchingGroupId: string;
  freightVendorId: string; // e.g., Walton Trucking
  freightVendorName: string;
  invoiceNumber: string;
  invoiceDate: string;
  loadDate: string; // Date of the haul
  quantity: number; // Tonnage or other unit
  unitOfMeasure: UnitOfMeasure;
  rate: number; // Rate per unit
  amount: number;
  matchedToLineId?: string; // Link to vendor invoice line
  status: FreightLineStatus;
  ticketNumber?: string; // Scale ticket or delivery ticket number
}

/**
 * Discrepancy record for tracking matching issues
 */
export interface MatchDiscrepancy {
  id: string;
  matchingGroupId: string;
  vendorLineId?: string;
  freightLineId?: string;
  type: DiscrepancyType;
  description: string;
  expectedValue?: number;
  actualValue?: number;
  variancePercent?: number;
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  isResolved: boolean;
}

/**
 * Freight Matching Group - groups vendor invoice with related freight invoices
 */
export interface FreightMatchingGroup {
  id: string;
  // Vendor Invoice Info (material supplier)
  vendorId: string;
  vendorName: string; // e.g., "Chelan Sand & Gravel"
  vendorInvoiceId: string;
  vendorInvoiceNumber: string;
  vendorInvoiceDate: string;
  vendorInvoiceAmount: number;
  // Tracking
  plantCode: string;
  jobId?: string;
  jobName?: string;
  // Lines
  vendorLines: VendorInvoiceLine[];
  freightLines: FreightInvoiceLine[];
  discrepancies: MatchDiscrepancy[];
  // Totals
  totalVendorAmount: number;
  totalFreightAmount: number;
  variance: number;
  variancePercent: number;
  // Status
  status: MatchingGroupStatus;
  matchedLinesCount: number;
  totalLinesCount: number;
  // Metadata
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
}

/**
 * Filter state for the matching list
 */
export interface FreightMatchingFilters {
  status: MatchingGroupStatus | "all";
  vendorId: string | "all";
  plantCode: string | "all";
  dateFrom: string | null;
  dateTo: string | null;
  searchQuery: string;
}

/**
 * Tolerance settings for auto-matching
 */
export interface MatchingTolerances {
  quantityVariancePercent: number; // Default 2%
  amountVariancePercent: number; // Default 2%
  dateDaysRange: number; // Days to match load dates (default 1)
}

/**
 * FreightMatching Store
 *
 * Manages third-party freight matching workflow per Implementation Plan Phase 1
 * - Match vendor material invoices (e.g., Chelan Sand & Gravel) with
 * - Freight carrier invoices (e.g., Walton Trucking, Pitton Trucking)
 */
class FreightMatchingStore {
  rootStore: RootStore;

  // Data - selectedGroupId is UI state (kept as observable)
  selectedGroupId: string | null = null;

  // UI State
  error: string | null = null;
  activeTab: "list" | "detail" = "list";

  // Filters
  filters: FreightMatchingFilters = {
    status: "all",
    vendorId: "all",
    plantCode: "all",
    dateFrom: null,
    dateTo: null,
    searchQuery: "",
  };

  // Tolerances for auto-matching
  tolerances: MatchingTolerances = {
    quantityVariancePercent: 2,
    amountVariancePercent: 2,
    dateDaysRange: 1,
  };

  // Form state for adding lines
  newVendorLine: Partial<VendorInvoiceLine> = {};
  newFreightLine: Partial<FreightInvoiceLine> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Getters ====================

  /**
   * Access the freight-matching-groups ServiceStore
   */
  get freightMatchingGroupsServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["freight-matching-groups"] as ServiceStore;
  }

  /**
   * Data from ServiceStore (replaces local observable array)
   */
  get matchingGroups(): FreightMatchingGroup[] {
    return (this.freightMatchingGroupsServiceStore?.entities as unknown as FreightMatchingGroup[]) || [];
  }

  /**
   * Loading state from ServiceStore (replaces local loading observable)
   */
  get loading(): boolean {
    return this.freightMatchingGroupsServiceStore?.isFindPending || false;
  }

  // ==================== Computed Properties ====================

  /**
   * Get filtered matching groups
   */
  get filteredGroups(): FreightMatchingGroup[] {
    return this.matchingGroups.filter((group) => {
      // Status filter
      if (this.filters.status !== "all" && group.status !== this.filters.status) {
        return false;
      }
      // Vendor filter
      if (this.filters.vendorId !== "all" && group.vendorId !== this.filters.vendorId) {
        return false;
      }
      // Plant filter
      if (this.filters.plantCode !== "all" && group.plantCode !== this.filters.plantCode) {
        return false;
      }
      // Date range filter
      if (this.filters.dateFrom) {
        const invoiceDate = new Date(group.vendorInvoiceDate);
        const fromDate = new Date(this.filters.dateFrom);
        if (invoiceDate < fromDate) return false;
      }
      if (this.filters.dateTo) {
        const invoiceDate = new Date(group.vendorInvoiceDate);
        const toDate = new Date(this.filters.dateTo);
        if (invoiceDate > toDate) return false;
      }
      // Search query
      if (this.filters.searchQuery) {
        const query = this.filters.searchQuery.toLowerCase();
        const matchesVendor = group.vendorName.toLowerCase().includes(query);
        const matchesInvoice = group.vendorInvoiceNumber.toLowerCase().includes(query);
        const matchesJob = group.jobName?.toLowerCase().includes(query);
        if (!matchesVendor && !matchesInvoice && !matchesJob) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Get the currently selected matching group
   */
  get selectedGroup(): FreightMatchingGroup | null {
    if (!this.selectedGroupId) return null;
    return this.matchingGroups.find((g) => g.id === this.selectedGroupId) || null;
  }

  /**
   * Summary metrics for the dashboard
   */
  get metrics() {
    const groups = this.matchingGroups;
    return {
      totalGroups: groups.length,
      pendingCount: groups.filter((g) => g.status === "pending").length,
      partialCount: groups.filter((g) => g.status === "partial").length,
      matchedCount: groups.filter((g) => g.status === "matched").length,
      discrepancyCount: groups.filter((g) => g.status === "discrepancy").length,
      totalVendorAmount: groups.reduce((sum, g) => sum + g.totalVendorAmount, 0),
      totalFreightAmount: groups.reduce((sum, g) => sum + g.totalFreightAmount, 0),
      totalVariance: groups.reduce((sum, g) => sum + Math.abs(g.variance), 0),
      unresolvedDiscrepancies: groups.reduce(
        (sum, g) => sum + g.discrepancies.filter((d) => !d.isResolved).length,
        0
      ),
    };
  }

  /**
   * Get unique vendors for filter dropdown
   */
  get uniqueVendors(): { id: string; name: string }[] {
    const vendorMap = new Map<string, string>();
    this.matchingGroups.forEach((g) => {
      vendorMap.set(g.vendorId, g.vendorName);
    });
    return Array.from(vendorMap.entries()).map(([id, name]) => ({ id, name }));
  }

  /**
   * Get unique plants for filter dropdown
   */
  get uniquePlants(): string[] {
    return [...new Set(this.matchingGroups.map((g) => g.plantCode))];
  }

  // ==================== Actions ====================

  /**
   * Load all freight matching data via ServiceStore
   */
  async loadData(): Promise<void> {
    this.error = null;
    try {
      await this.freightMatchingGroupsServiceStore?.find({ query: { $limit: 200 } });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load freight matching data";
      });
    }
  }

  /**
   * Select a matching group to view/edit
   */
  selectGroup(groupId: string | null): void {
    this.selectedGroupId = groupId;
    if (groupId) {
      this.activeTab = "detail";
    }
  }

  /**
   * Set active tab
   */
  setActiveTab(tab: "list" | "detail"): void {
    this.activeTab = tab;
    if (tab === "list") {
      this.selectedGroupId = null;
    }
  }

  /**
   * Update filters
   */
  setFilter<K extends keyof FreightMatchingFilters>(
    key: K,
    value: FreightMatchingFilters[K]
  ): void {
    this.filters[key] = value;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      status: "all",
      vendorId: "all",
      plantCode: "all",
      dateFrom: null,
      dateTo: null,
      searchQuery: "",
    };
  }

  // ==================== Line Management ====================

  /**
   * Add a vendor invoice line to the selected group via ServiceStore patch
   */
  async addVendorLine(line: Omit<VendorInvoiceLine, "id" | "matchingGroupId" | "status">): Promise<void> {
    if (!this.selectedGroup) return;

    const newLine: VendorInvoiceLine = {
      ...line,
      id: `vl-${Date.now()}`,
      matchingGroupId: this.selectedGroup.id,
      status: "pending",
    };

    const updatedVendorLines = [...this.selectedGroup.vendorLines, newLine];
    const updatedGroup = this.recalculateGroupTotalsData({
      ...this.selectedGroup,
      vendorLines: updatedVendorLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroup);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to add vendor line";
      });
    }
  }

  /**
   * Add a freight invoice line to the selected group via ServiceStore patch
   */
  async addFreightLine(line: Omit<FreightInvoiceLine, "id" | "matchingGroupId" | "status">): Promise<void> {
    if (!this.selectedGroup) return;

    const newLine: FreightInvoiceLine = {
      ...line,
      id: `fl-${Date.now()}`,
      matchingGroupId: this.selectedGroup.id,
      status: "pending",
    };

    const updatedFreightLines = [...this.selectedGroup.freightLines, newLine];
    const updatedGroup = this.recalculateGroupTotalsData({
      ...this.selectedGroup,
      freightLines: updatedFreightLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroup);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to add freight line";
      });
    }
  }

  /**
   * Remove a vendor line via ServiceStore patch
   */
  async removeVendorLine(lineId: string): Promise<void> {
    if (!this.selectedGroup) return;

    // Unlink any matched freight line
    const line = this.selectedGroup.vendorLines.find((l) => l.id === lineId);
    const updatedFreightLines = this.selectedGroup.freightLines.map((f) => {
      if (line?.matchedFreightLineId && f.id === line.matchedFreightLineId) {
        return { ...f, matchedToLineId: undefined, status: "pending" as FreightLineStatus };
      }
      return f;
    });

    const updatedVendorLines = this.selectedGroup.vendorLines.filter((l) => l.id !== lineId);
    const updatedGroup = this.recalculateGroupTotalsData({
      ...this.selectedGroup,
      vendorLines: updatedVendorLines,
      freightLines: updatedFreightLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroup);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to remove vendor line";
      });
    }
  }

  /**
   * Remove a freight line via ServiceStore patch
   */
  async removeFreightLine(lineId: string): Promise<void> {
    if (!this.selectedGroup) return;

    // Unlink any matched vendor line
    const line = this.selectedGroup.freightLines.find((l) => l.id === lineId);
    const updatedVendorLines = this.selectedGroup.vendorLines.map((v) => {
      if (line?.matchedToLineId && v.id === line.matchedToLineId) {
        return { ...v, matchedFreightLineId: undefined, status: "pending" as FreightLineStatus };
      }
      return v;
    });

    const updatedFreightLines = this.selectedGroup.freightLines.filter((l) => l.id !== lineId);
    const updatedGroup = this.recalculateGroupTotalsData({
      ...this.selectedGroup,
      vendorLines: updatedVendorLines,
      freightLines: updatedFreightLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroup);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to remove freight line";
      });
    }
  }

  // ==================== Matching Logic ====================

  /**
   * Manually link a vendor line to a freight line via ServiceStore patch
   */
  async linkLines(vendorLineId: string, freightLineId: string): Promise<void> {
    if (!this.selectedGroup) return;

    let updatedVendorLines = [...this.selectedGroup.vendorLines];
    let updatedFreightLines = [...this.selectedGroup.freightLines];

    const vendorLine = updatedVendorLines.find((l) => l.id === vendorLineId);
    const freightLine = updatedFreightLines.find((l) => l.id === freightLineId);

    if (!vendorLine || !freightLine) return;

    // Clear any existing links
    if (vendorLine.matchedFreightLineId) {
      updatedFreightLines = updatedFreightLines.map((f) =>
        f.id === vendorLine.matchedFreightLineId
          ? { ...f, matchedToLineId: undefined, status: "pending" as FreightLineStatus }
          : f
      );
    }
    if (freightLine.matchedToLineId) {
      updatedVendorLines = updatedVendorLines.map((v) =>
        v.id === freightLine.matchedToLineId
          ? { ...v, matchedFreightLineId: undefined, status: "pending" as FreightLineStatus }
          : v
      );
    }

    // Check for discrepancy
    const quantityVariance = Math.abs(vendorLine.quantity - freightLine.quantity) / vendorLine.quantity * 100;
    const hasDiscrepancy = quantityVariance > this.tolerances.quantityVariancePercent;
    const matchStatus: FreightLineStatus = hasDiscrepancy ? "discrepancy" : "matched";

    // Create new link
    updatedVendorLines = updatedVendorLines.map((v) =>
      v.id === vendorLineId
        ? { ...v, matchedFreightLineId: freightLineId, status: matchStatus }
        : v
    );
    updatedFreightLines = updatedFreightLines.map((f) =>
      f.id === freightLineId
        ? { ...f, matchedToLineId: vendorLineId, status: matchStatus }
        : f
    );

    let updatedDiscrepancies = [...this.selectedGroup.discrepancies];
    if (hasDiscrepancy) {
      updatedDiscrepancies.push({
        id: `disc-${Date.now()}`,
        matchingGroupId: this.selectedGroup.id,
        vendorLineId,
        freightLineId,
        type: "quantity_mismatch",
        description: `Quantity mismatch: Vendor ${vendorLine.quantity} vs Freight ${freightLine.quantity} ${vendorLine.unitOfMeasure}`,
        expectedValue: vendorLine.quantity,
        actualValue: freightLine.quantity,
        variancePercent: quantityVariance,
        isResolved: false,
      });
    }

    const updatedGroupData = this.computeGroupStatus({
      ...this.selectedGroup,
      vendorLines: updatedVendorLines,
      freightLines: updatedFreightLines,
      discrepancies: updatedDiscrepancies,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroupData);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to link lines";
      });
    }
  }

  /**
   * Unlink a vendor line from its freight line via ServiceStore patch
   */
  async unlinkVendorLine(vendorLineId: string): Promise<void> {
    if (!this.selectedGroup) return;

    const vendorLine = this.selectedGroup.vendorLines.find((l) => l.id === vendorLineId);
    if (!vendorLine?.matchedFreightLineId) return;

    const updatedVendorLines = this.selectedGroup.vendorLines.map((v) =>
      v.id === vendorLineId
        ? { ...v, matchedFreightLineId: undefined, status: "pending" as FreightLineStatus }
        : v
    );
    const updatedFreightLines = this.selectedGroup.freightLines.map((f) =>
      f.id === vendorLine.matchedFreightLineId
        ? { ...f, matchedToLineId: undefined, status: "pending" as FreightLineStatus }
        : f
    );

    const updatedGroupData = this.computeGroupStatus({
      ...this.selectedGroup,
      vendorLines: updatedVendorLines,
      freightLines: updatedFreightLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroupData);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to unlink lines";
      });
    }
  }

  /**
   * Run auto-matching algorithm on a group and persist via ServiceStore
   */
  async runAutoMatch(group: FreightMatchingGroup): Promise<void> {
    const updatedVendorLines = [...group.vendorLines];
    const updatedFreightLines = [...group.freightLines];

    const unmatchedVendorLines = updatedVendorLines.filter((l) => !l.matchedFreightLineId);
    const unmatchedFreightLines = updatedFreightLines.filter((l) => !l.matchedToLineId);

    for (const vendorLine of unmatchedVendorLines) {
      // Find freight lines with matching load date and similar quantity
      const candidates = unmatchedFreightLines.filter((freightLine) => {
        // Check if already matched in this iteration
        if (freightLine.matchedToLineId) return false;

        // Date matching (within tolerance)
        const vendorDate = new Date(vendorLine.loadDate);
        const freightDate = new Date(freightLine.loadDate);
        const daysDiff = Math.abs(vendorDate.getTime() - freightDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff > this.tolerances.dateDaysRange) return false;

        // Quantity matching (within tolerance)
        const normalizedVendorQty = this.normalizeQuantity(vendorLine.quantity, vendorLine.unitOfMeasure);
        const normalizedFreightQty = this.normalizeQuantity(freightLine.quantity, freightLine.unitOfMeasure);
        const qtyVariance = Math.abs(normalizedVendorQty - normalizedFreightQty) / normalizedVendorQty * 100;
        if (qtyVariance > this.tolerances.quantityVariancePercent) return false;

        return true;
      });

      if (candidates.length === 1) {
        // Exact match found - auto-link
        const freightLine = candidates[0];
        const vIdx = updatedVendorLines.findIndex((v) => v.id === vendorLine.id);
        const fIdx = updatedFreightLines.findIndex((f) => f.id === freightLine.id);
        if (vIdx !== -1) {
          updatedVendorLines[vIdx] = { ...updatedVendorLines[vIdx], matchedFreightLineId: freightLine.id, status: "matched" };
        }
        if (fIdx !== -1) {
          updatedFreightLines[fIdx] = { ...updatedFreightLines[fIdx], matchedToLineId: vendorLine.id, status: "matched" };
        }
      }
    }

    const updatedGroupData = this.computeGroupStatus({
      ...group,
      vendorLines: updatedVendorLines,
      freightLines: updatedFreightLines,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(group.id, updatedGroupData);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to run auto-match";
      });
    }
  }

  /**
   * Normalize quantity to tons for comparison
   */
  normalizeQuantity(quantity: number, unit: UnitOfMeasure): number {
    switch (unit) {
      case "lbs":
        return quantity / 2000;
      case "tons":
      case "each":
      default:
        return quantity;
    }
  }

  /**
   * Convert quantity between units
   */
  convertQuantity(quantity: number, fromUnit: UnitOfMeasure, toUnit: UnitOfMeasure): number {
    if (fromUnit === toUnit) return quantity;

    // Convert to tons first
    let inTons = quantity;
    if (fromUnit === "lbs") {
      inTons = quantity / 2000;
    }

    // Convert from tons to target unit
    if (toUnit === "lbs") {
      return inTons * 2000;
    }
    return inTons;
  }

  // ==================== Discrepancy Management ====================

  /**
   * Add a discrepancy to the selected group via ServiceStore patch
   */
  async addDiscrepancy(discrepancy: Omit<MatchDiscrepancy, "id" | "matchingGroupId" | "isResolved">): Promise<void> {
    if (!this.selectedGroup) return;

    const newDiscrepancy: MatchDiscrepancy = {
      ...discrepancy,
      id: `disc-${Date.now()}`,
      matchingGroupId: this.selectedGroup.id,
      isResolved: false,
    };

    const updatedDiscrepancies = [...this.selectedGroup.discrepancies, newDiscrepancy];
    const updatedGroupData = this.computeGroupStatus({
      ...this.selectedGroup,
      discrepancies: updatedDiscrepancies,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroupData);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to add discrepancy";
      });
    }
  }

  /**
   * Resolve a discrepancy via ServiceStore patch
   */
  async resolveDiscrepancy(discrepancyId: string, resolution: string): Promise<void> {
    if (!this.selectedGroup) return;

    const user = this.rootStore.authStore?.user;
    const resolvedBy = user ? `${user.firstName} ${user.lastName}` : "Unknown";

    const updatedDiscrepancies = this.selectedGroup.discrepancies.map((d) =>
      d.id === discrepancyId
        ? {
            ...d,
            resolution,
            resolvedBy,
            resolvedAt: new Date().toISOString(),
            isResolved: true,
          }
        : d
    );

    const updatedGroupData = this.computeGroupStatus({
      ...this.selectedGroup,
      discrepancies: updatedDiscrepancies,
    });

    try {
      await this.freightMatchingGroupsServiceStore?.patch(this.selectedGroup.id, updatedGroupData);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to resolve discrepancy";
      });
    }
  }

  // ==================== Group Management ====================

  /**
   * Create a new matching group from a vendor invoice via ServiceStore create
   */
  async createMatchingGroup(vendorInvoice: {
    vendorId: string;
    vendorName: string;
    invoiceId: string;
    invoiceNumber: string;
    invoiceDate: string;
    invoiceAmount: number;
    plantCode: string;
    jobId?: string;
    jobName?: string;
  }): Promise<FreightMatchingGroup | null> {
    const newGroup: Omit<FreightMatchingGroup, "id"> = {
      vendorId: vendorInvoice.vendorId,
      vendorName: vendorInvoice.vendorName,
      vendorInvoiceId: vendorInvoice.invoiceId,
      vendorInvoiceNumber: vendorInvoice.invoiceNumber,
      vendorInvoiceDate: vendorInvoice.invoiceDate,
      vendorInvoiceAmount: vendorInvoice.invoiceAmount,
      plantCode: vendorInvoice.plantCode,
      jobId: vendorInvoice.jobId,
      jobName: vendorInvoice.jobName,
      vendorLines: [],
      freightLines: [],
      discrepancies: [],
      totalVendorAmount: 0,
      totalFreightAmount: 0,
      variance: 0,
      variancePercent: 0,
      status: "pending",
      matchedLinesCount: 0,
      totalLinesCount: 0,
      createdAt: new Date().toISOString(),
      createdBy: this.rootStore.authStore?.user
        ? `${this.rootStore.authStore.user.firstName} ${this.rootStore.authStore.user.lastName}`
        : "Unknown",
      updatedAt: new Date().toISOString(),
    };

    try {
      const created = await this.freightMatchingGroupsServiceStore?.create(newGroup);
      return (created as unknown as FreightMatchingGroup) || null;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to create matching group";
      });
      return null;
    }
  }

  /**
   * Mark a group as complete via ServiceStore patch
   */
  async markGroupComplete(groupId: string): Promise<void> {
    const group = this.matchingGroups.find((g) => g.id === groupId);
    if (!group) return;

    // Check if all lines are matched
    const unmatchedVendor = group.vendorLines.filter((l) => !l.matchedFreightLineId).length;
    const unmatchedFreight = group.freightLines.filter((l) => !l.matchedToLineId).length;
    const unresolvedDiscrepancies = group.discrepancies.filter((d) => !d.isResolved).length;

    if (unmatchedVendor > 0 || unmatchedFreight > 0 || unresolvedDiscrepancies > 0) {
      this.error = `Cannot complete: ${unmatchedVendor} unmatched vendor lines, ${unmatchedFreight} unmatched freight lines, ${unresolvedDiscrepancies} unresolved discrepancies`;
      return;
    }

    const completeUser = this.rootStore.authStore?.user;
    try {
      await this.freightMatchingGroupsServiceStore?.patch(groupId, {
        status: "matched",
        completedAt: new Date().toISOString(),
        completedBy: completeUser ? `${completeUser.firstName} ${completeUser.lastName}` : "Unknown",
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to mark group complete";
      });
    }
  }

  /**
   * Recalculate totals for a group data object (pure function, no mutation)
   */
  private recalculateGroupTotalsData(group: Partial<FreightMatchingGroup>): Partial<FreightMatchingGroup> {
    const vendorLines = group.vendorLines || [];
    const freightLines = group.freightLines || [];
    const totalVendorAmount = vendorLines.reduce((sum, l) => sum + l.amount, 0);
    const totalFreightAmount = freightLines.reduce((sum, l) => sum + l.amount, 0);
    const variance = totalVendorAmount - totalFreightAmount;
    const variancePercent = totalVendorAmount > 0 ? (variance / totalVendorAmount) * 100 : 0;
    const totalLinesCount = vendorLines.length;
    const matchedLinesCount = vendorLines.filter((l) => l.matchedFreightLineId).length;

    return {
      ...group,
      totalVendorAmount,
      totalFreightAmount,
      variance,
      variancePercent,
      totalLinesCount,
      matchedLinesCount,
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Compute status of a group based on its lines (pure function, no mutation)
   */
  private computeGroupStatus(group: Partial<FreightMatchingGroup>): Partial<FreightMatchingGroup> {
    const recalculated = this.recalculateGroupTotalsData(group);
    const vendorLines = recalculated.vendorLines || [];
    const freightLines = recalculated.freightLines || [];
    const discrepancies = recalculated.discrepancies || [];

    const hasDiscrepancies = discrepancies.some((d) => !d.isResolved);
    const allVendorMatched = vendorLines.every((l) => l.matchedFreightLineId);
    const allFreightMatched = freightLines.every((l) => l.matchedToLineId);
    const matchedLinesCount = recalculated.matchedLinesCount || 0;

    let status: MatchingGroupStatus;
    if (hasDiscrepancies) {
      status = "discrepancy";
    } else if (allVendorMatched && allFreightMatched && vendorLines.length > 0) {
      status = "matched";
    } else if (matchedLinesCount > 0) {
      status = "partial";
    } else {
      status = "pending";
    }

    return {
      ...recalculated,
      status,
    };
  }

  // ==================== Dialog Helpers ====================

  /**
   * Open the create matching group dialog
   */
  openCreateGroupDialog(): void {
    this.rootStore.routerStore.openDialog({
      dialogName: "createMatchingGroup",
    });
  }

  /**
   * Open the add vendor line dialog
   */
  openAddVendorLineDialog(): void {
    this.newVendorLine = {
      lineNumber: (this.selectedGroup?.vendorLines.length || 0) + 1,
      loadDate: new Date().toISOString().split("T")[0],
      unitOfMeasure: "tons",
    };
    this.rootStore.routerStore.openDialog({
      dialogName: "addVendorLine",
    });
  }

  /**
   * Open the add freight line dialog
   */
  openAddFreightLineDialog(): void {
    this.newFreightLine = {
      loadDate: new Date().toISOString().split("T")[0],
      invoiceDate: new Date().toISOString().split("T")[0],
      unitOfMeasure: "tons",
    };
    this.rootStore.routerStore.openDialog({
      dialogName: "addFreightLine",
    });
  }

  /**
   * Open the resolve discrepancy dialog
   */
  openResolveDiscrepancyDialog(discrepancyId: string): void {
    this.rootStore.routerStore.openDialog({
      dialogName: "resolveDiscrepancy",
      dialogContext: discrepancyId,
    });
  }

  // ==================== Utility Methods ====================

  /**
   * Get status label for display
   */
  getStatusLabel(status: MatchingGroupStatus): string {
    const labels: Record<MatchingGroupStatus, string> = {
      pending: "Pending",
      partial: "Partial Match",
      matched: "Matched",
      discrepancy: "Discrepancy",
    };
    return labels[status];
  }

  /**
   * Get status color for MUI chips
   */
  getStatusColor(status: MatchingGroupStatus): "warning" | "info" | "success" | "error" {
    const colors: Record<MatchingGroupStatus, "warning" | "info" | "success" | "error"> = {
      pending: "warning",
      partial: "info",
      matched: "success",
      discrepancy: "error",
    };
    return colors[status];
  }

  /**
   * Get line status icon
   */
  getLineStatusIcon(status: FreightLineStatus): "check" | "warning" | "error" {
    const icons: Record<FreightLineStatus, "check" | "warning" | "error"> = {
      matched: "check",
      pending: "warning",
      discrepancy: "error",
    };
    return icons[status];
  }
}

export default FreightMatchingStore;
